import "./index.less";
import React, {
  CSSProperties,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import {
  TabsPlusButtonType,
  TabsPlusButtonUIProps,
} from "./TabsPlusButtonUI/TabsPlusButtonUI";

const getHeadChildren = (props: any) => {
  const { children } = props;
  const headChildren = [];
  React.Children.forEach(children, (child: ReactElement) => {
    if (
      child &&
      child.type &&
      (child as ReactElement<TabsPlusButtonUIProps, TabsPlusButtonType>).type
        .isTabsPlusButton
    ) {
      headChildren.push(child);
    }
  });
  return headChildren as ReactElement<
    TabsPlusButtonUIProps,
    TabsPlusButtonType
  >[];
};

export type ModelType = "fill-width" | "auto";

export interface IFTabsPlusContext {
  index?: any;
  interval?: number;
  onSelect?: (index: number) => void;
  model?: ModelType;
  onInit?: (ref: any, index?: number) => void;
  disabled?: boolean;
}

export const TabsPlusContext = React.createContext<IFTabsPlusContext>({
  index: 0,
});

export interface TabsPlusUIProps {
  className?: string;
  model?: ModelType;
  value?: any;
  defaultValue?: any;
  interval?: number;
  onSelect?: (index: any) => void;
  style?: CSSProperties | undefined;
  disabled?: boolean;
}

const TabsPlusUI = (props: PropsWithChildren<TabsPlusUIProps>) => {
  const {
    className,
    model = "fill-width",
    defaultValue = 0,
    onSelect,
    interval,
    style = {},
    disabled,
    value,
  } = props;
  const prefixCls = "tabs-plus-ui";
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headChildren = getHeadChildren(props);
  const [currentIndex, setCurrentIndex] = useState<number>(defaultValue);

  useEffect(() => {
    if (typeof defaultValue !== "undefined") {
      setCurrentIndex(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (typeof value !== "undefined" && value !== null) {
      setCurrentIndex(value);
    }
  }, [value]);

  // 滚动到指定tab
  const scrollToTab = useCallback(
    (index: number) => {
      if (
        model !== "auto" ||
        !scrollContainerRef.current ||
        !tabRefs.current[index]
      )
        return;

      const container = scrollContainerRef.current;
      const targetTab = tabRefs.current[index];

      if (!targetTab) return;

      // 计算tab相对于container的位置
      const tabOffsetLeft = targetTab.offsetLeft;
      const tabWidth = targetTab.offsetWidth;
      const containerWidth = container.clientWidth;

      // 目标位置：让tab居中显示
      const targetScrollLeft =
        tabOffsetLeft - containerWidth / 2 + tabWidth / 2;

      // 限制滚动范围
      const maxScrollLeft = container.scrollWidth - containerWidth;
      const finalScrollLeft = Math.max(
        0,
        Math.min(targetScrollLeft, maxScrollLeft)
      );

      container.scrollTo({
        left: finalScrollLeft,
        behavior: "smooth",
      });
    },
    [model]
  );

  // 处理tab点击
  const handleClick = useCallback(
    (index: number) => {
      if (disabled) return;

      setCurrentIndex(index);
      onSelect && onSelect(index);

      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        scrollToTab(index);
      });
    },
    [disabled, onSelect, scrollToTab]
  );

  // 初始化ref回调
  const onInit = useCallback((ref: HTMLDivElement | null, index: number) => {
    tabRefs.current[index] = ref;
  }, []);

  // 监听内容变化，滚动到当前选中的tab
  useEffect(() => {
    if (model === "auto" && scrollContainerRef.current) {
      // 滚动到当前选中的tab
      setTimeout(() => {
        scrollToTab(currentIndex);
      }, 100);
    }
  }, [model, currentIndex, scrollToTab, headChildren.length]);

  const headContext: IFTabsPlusContext = {
    index: currentIndex,
    onSelect: handleClick,
    interval: interval,
    model: model,
    onInit: onInit,
    disabled: disabled,
  };

  if (model === "fill-width") {
    return (
      <div
        className={classNames([className, prefixCls, `${prefixCls}-${model}`])}
        style={style}
        ref={containerRef}
      >
        <TabsPlusContext.Provider value={{ ...headContext }}>
          {headChildren.map((child, index) =>
            React.cloneElement(child, {
              key: child.key || index,
              _internalIndex: index,
            })
          )}
        </TabsPlusContext.Provider>
      </div>
    );
  }

  // auto模式的实现 - 简化版本，直接显示滚动条
  return (
    <div
      className={classNames([className, prefixCls, `${prefixCls}-${model}`])}
      style={style}
      ref={containerRef}
    >
      <div className={`${prefixCls}-scroll-container`} ref={scrollContainerRef}>
        <TabsPlusContext.Provider value={{ ...headContext }}>
          {headChildren.map((child, index) =>
            React.cloneElement(child, {
              key: child.key || index,
              _internalIndex: index,
            })
          )}
        </TabsPlusContext.Provider>
      </div>
    </div>
  );
};

export default React.memo(TabsPlusUI);
